/**
 * The one confirm gesture, settled the same way from Start and from Work.
 *
 * A Work card and the quote box are two doors to the same money moment, so
 * they share the request and the sentences: same server route, same
 * `expectedAmountDueCents`, same words for every outcome. Since 2026-09-26
 * (Alan: "why would someone click confirm just to open a popup?") neither door
 * opens a sheet - both hold (or swipe) SwipeToConfirm in place, with what is
 * charged and how disclosed under it.
 *
 * @module web/screens/app/confirmSheet
 */
import { raw, type RawResponse } from "../../api/client.ts";
import type { BriefSubmissionResponse } from "../../api/types.ts";
import type { SwipeResult } from "./work/SwipeToConfirm.tsx";
import { isConfirmationInProgress } from "./confirmation.ts";

/** Fired on `window` when a confirmation went through; the shell recounts the badge. */
export const BRIEFS_CHANGED = "briefs:changed";

export function announceBriefsChanged(): void {
  window.dispatchEvent(new Event(BRIEFS_CHANGED));
}

export type ConfirmResponse = RawResponse<{
  charged?: boolean; declined?: boolean; error?: string; code?: string; processing?: boolean; submission?: BriefSubmissionResponse;
}>;

/**
 * The tap. Sends the amount the customer saw, so a quote that moved underneath
 * answers 409 quote_changed instead of charging a number nobody approved.
 * Never throws: no connection is status 0, a sentence like any other.
 */
export async function postConfirm(url: string, expectedAmountDueCents: number | null): Promise<ConfirmResponse> {
  try {
    const allowDuplicate = duplicateAcknowledged.has(url) || undefined;
    const res: ConfirmResponse = await raw(url, { method: "POST", body: JSON.stringify({ expectedAmountDueCents, allowDuplicate }) });
    // The server said "you already paid for this script"; its sentence is the
    // refusal, and the NEXT swipe on this briefing means "yes, an extra video".
    if (res.status === 409 && res.body?.code === "possible_duplicate") duplicateAcknowledged.add(url);
    return res;
  } catch {
    return { status: 0, body: null };
  }
}

/** Confirm URLs whose duplicate warning the customer has seen (this page load only). */
const duplicateAcknowledged = new Set<string>();

/**
 * Only a completed operation is success. A conflict can also mean the server
 * refused an unpriced/not-ready brief; pending work remains observable.
 */
export function confirmResult(res: ConfirmResponse, plural: boolean): SwipeResult {
  if (res.status === 202 || res.body?.processing || isConfirmationInProgress(res.body?.submission?.status ?? null)) {
    return { ok: false, message: "Confirmation is still processing. Check Work for updates." };
  }
  if (res.status === 409) return res.body?.submission?.status === "confirmed"
    ? { ok: true, message: "Already confirmed" }
    : { ok: false, message: res.body?.error ?? "Could not confirm. Refresh this briefing before trying again." };
  if (res.status === 200) {
    const sent = plural ? "Sent to your editors" : "Sent to your editor";
    return { ok: true, message: res.body?.charged ? `Paid · ${sent.toLowerCase()}` : sent };
  }
  if (res.status === 402) {
    return { ok: false, message: res.body?.declined ? "Payment failed - try again" : "Your balance doesn't cover this - top up first" };
  }
  if (res.status === 0) return { ok: false, message: "No connection - try again" };
  return { ok: false, message: res.body?.error ?? "Could not confirm - try again, you won't be charged twice" };
}
